import React from 'react';
import { PlanAxisLabel } from 'types';
import { PLAN_AXIS_LABEL_MAX_LENGTH } from 'utils/planAxisLabels';
import { timelineCanvasThemes, activeTimelineCanvasTheme } from 'theme';

export type PlanAxisLabelCellProps = {
    /** True for a frozen-COLUMN cell (the release axis, cut by horizontal lines — matches a
     * PlanRegion's rowIndex) — rendered with vertical (rotated) text, since the column is a
     * narrow vertical strip. False for a frozen-ROW cell (the priority axis, cut by vertical
     * lines — matches columnIndex) — plain horizontal text, since the row is short but wide. */
    vertical: boolean;
    /** The persisted label for this segment, or undefined if the user hasn't set one yet. */
    label: PlanAxisLabel | undefined;
    /** This segment's own start coordinate (canvas units, x for a row cell / y for a column
     * cell) — the origin `visibleMin`/`visibleMax` below are offset against to place the sticky
     * inner box within this cell's own local coordinate space. */
    min: number;
    /** The portion of this segment currently on screen (canvas units, same coordinate space as
     * `min`), already clamped to the segment's own bounds by GridCanvas — equal to the segment's
     * full extent whenever it entirely fits in the viewport. Used to keep the actual input
     * centered in whatever's visible right now, rather than in the segment's full extent (which
     * can run off screen while zoomed in — see the `plan-axis-label-cell__sticky` wrapper below). */
    visibleMin: number;
    visibleMax: number;
    onTextChange: (text: string) => void;
};

/** Cell background — sourced from the active Timeline theme (theme.ts's `timelineCanvasThemes`)
 * so it reads as plain chrome consistent with whichever background/grid look is currently active,
 * rather than a value chosen independently here. There's no UI to change this per segment anymore
 * (the color swatch button was removed); a `label.color` set by an older build of this feature
 * still renders if present, purely for backward compatibility with already-persisted data. */
const DEFAULT_CELL_BACKGROUND = timelineCanvasThemes[activeTimelineCanvasTheme].frozenCellBackground;

/**
 * One editable cell inside the Timeline's frozen row/column — one per region column/row (see
 * PlanRegion, types.ts) — letting the user name what that position along the axis represents
 * (e.g. a release name, a priority tier). Rendered by TimelineCanvas via GridCanvas's
 * `renderFrozenRowSegment`/`renderFrozenColumnSegment`; the persisted text lives in PlanPage's
 * `axisLabels` and flows in as `label`, with edits flowing back out through `onTextChange`
 * (PlanPage does the actual `upsertAxisLabel` write — utils/planAxisLabels.ts).
 *
 * The outer `.plan-axis-label-cell` div fills the ENTIRE segment (so its background reads as a
 * full-width/height strip, and its trailing border — drawn by GridCanvas itself, not here —
 * lines up exactly with the drawn line at that boundary). The actual input lives in a
 * `.plan-axis-label-cell__sticky` inner box instead, positioned/sized to just the currently
 * VISIBLE slice of the segment (`visibleMin`/`visibleMax`, relative to the segment's own `min`) —
 * so if the segment is wide/tall enough to run off screen while zoomed in, the input stays
 * centered in whatever's actually on screen rather than sitting off in the unreachable part.
 */
export default function PlanAxisLabelCell({ vertical, label, min, visibleMin, visibleMax, onTextChange }: PlanAxisLabelCellProps) {
    const text = label?.text ?? '';
    const color = label?.color ?? null;

    const stickyOffset = Math.max(0, visibleMin - min);
    const stickySize = Math.max(0, visibleMax - visibleMin);
    const stickyStyle: React.CSSProperties = vertical
        ? { position: 'absolute', left: 0, width: '100%', top: stickyOffset, height: stickySize }
        : { position: 'absolute', top: 0, height: '100%', left: stickyOffset, width: stickySize };

    // Stops a click/drag inside the cell from being read as a canvas pan, or a line-placement
    // click, by the SVG underneath — the same guard every other floating control on this canvas
    // needs (see PlanPage's line color popover inputs).
    const stopMouseDown = (e: React.MouseEvent) => e.stopPropagation();

    return (
        <div
            className={`plan-axis-label-cell ${vertical ? 'plan-axis-label-cell--vertical' : 'plan-axis-label-cell--horizontal'}`}
            style={{ background: color ?? DEFAULT_CELL_BACKGROUND }}
        >
            <div className='plan-axis-label-cell__sticky' style={stickyStyle}>
                <input
                    type='text'
                    className='plan-axis-label-cell__input'
                    value={text}
                    maxLength={PLAN_AXIS_LABEL_MAX_LENGTH}
                    placeholder='Label'
                    onChange={(e) => onTextChange(e.target.value)}
                    onMouseDown={stopMouseDown}
                />
            </div>
        </div>
    );
}
